// AutoPrintService.js - Servicio de impresion automatica de comandas desde app
import API from '../utils/api';
import comandaService from './ComandaService';

class AutoPrintService {
  constructor() {
    this.intervalo = null;
    this.procesando = false;
    this.polling_ms = 3000; // 3 segundos
    this.activo = false;
  }

  log(mensaje, datos) {
    if (datos !== undefined) {
      console.log(`[AutoPrintService] ${mensaje}`, datos);
    } else {
      console.log(`[AutoPrintService] ${mensaje}`);
    }
  }

  /**
   * Resuelve el nombre real de la impresora.
   * Si es "Local", "local", vacío o null → usa la impresora predeterminada del sistema.
   */
  resolverImpresora(nombreImpresora) {
    const nombre = (nombreImpresora || '').trim().toLowerCase();

    // Si es "local", vacío o no definido → usar la predeterminada
    if (!nombre || nombre === 'local') {
      const configDefault = comandaService.configuracionImpresoras?.['DEFAULT'];
      if (configDefault && configDefault.nombreImpresora) {
        this.log(`Impresora "${nombreImpresora || '(vacío)'}" → predeterminada: ${configDefault.nombreImpresora}`);
        return configDefault.nombreImpresora;
      }
      // Si no hay config DEFAULT, intentar buscar la default en la lista de impresoras
      const impresoraDefault = comandaService.impresorasDisponibles?.find(imp => imp.isDefault);
      if (impresoraDefault) {
        this.log(`Impresora "${nombreImpresora || '(vacío)'}" → predeterminada del sistema: ${impresoraDefault.name}`);
        return impresoraDefault.name;
      }
      this.log(`Impresora "${nombreImpresora || '(vacío)'}" es local pero no se encontró predeterminada`);
      return nombreImpresora || 'DEFAULT';
    }

    return nombreImpresora;
  }

  async iniciar() {
    if (this.activo) {
      this.log('El servicio ya esta activo');
      return;
    }

    this.activo = true;
    this.log('Esperando a que ComandaService se inicialice...');

    // Esperar a que ComandaService tenga las impresoras cargadas
    await this.esperarComandaService();

    this.log(`Servicio iniciado - polling cada ${this.polling_ms / 1000}s`);

    // Ejecutar inmediatamente la primera vez
    this.procesarPendientes();

    // Configurar intervalo
    this.intervalo = setInterval(() => {
      this.procesarPendientes();
    }, this.polling_ms);
  }

  /**
   * Espera hasta que ComandaService tenga la configuracion de impresoras cargada.
   * Reintenta cada 1 segundo, maximo 15 intentos.
   */
  async esperarComandaService() {
    const maxIntentos = 15;
    for (let i = 0; i < maxIntentos; i++) {
      if (comandaService.configuracionCargada) {
        this.log('ComandaService listo');
        return;
      }
      await new Promise(r => setTimeout(r, 1000));
    }
    this.log('ComandaService no se inicializo a tiempo, continuando de todos modos');
  }

  detener() {
    if (this.intervalo) {
      clearInterval(this.intervalo);
      this.intervalo = null;
    }
    this.activo = false;
    this.log('Servicio detenido');
  }

  /**
   * Agrupa items por persona y por platillo, sumando cantidades.
   * Inserta separadores "--- PERSONA X ---" entre grupos de personas.
   * Retorna un array en el formato que espera ComandaService.imprimirComanda.
   */
  agruparPorPersonaYPlatillo(items) {
    // Agrupar por persona
    const porPersona = {};
    items.forEach(item => {
      const persona = item.numeroPersona || 0;
      if (!porPersona[persona]) {
        porPersona[persona] = [];
      }
      porPersona[persona].push(item);
    });

    // Ordenar personas (0 primero si existe, luego 1, 2, 3...)
    const personas = Object.keys(porPersona).map(Number).sort((a, b) => a - b);
    const tieneMultiplesPersonas = personas.length > 1 || (personas.length === 1 && personas[0] > 0);

    const productosFinales = [];

    personas.forEach(persona => {
      // Insertar separador de persona solo para persona > 0
      // Persona 0 = "Común", no lleva separador en la comanda
      if (tieneMultiplesPersonas && persona > 0) {
        productosFinales.push({
          nombre: `━━ PERSONA ${persona} ━━`,
          cantidad: '',
          observaciones: [],
          instrucciones: '',
          esSeparador: true
        });
      }

      // Agrupar platillos iguales dentro de esta persona (por idPlatillo)
      const platillosAgrupados = {};
      porPersona[persona].forEach(item => {
        const keyPlatillo = item.idPlatillo || item.nombre;
        if (!platillosAgrupados[keyPlatillo]) {
          platillosAgrupados[keyPlatillo] = {
            nombre: item.nombre,
            cantidad: 0,
            observaciones: [],
            instrucciones: ''
          };
        }
        platillosAgrupados[keyPlatillo].cantidad += item.cantidad;

        // Acumular observaciones sin duplicar
        if (item.observaciones && item.observaciones.length > 0) {
          item.observaciones.forEach(obs => {
            if (obs && !platillosAgrupados[keyPlatillo].observaciones.includes(obs)) {
              platillosAgrupados[keyPlatillo].observaciones.push(obs);
            }
          });
        }
        if (item.instrucciones && item.instrucciones.trim()) {
          if (platillosAgrupados[keyPlatillo].instrucciones) {
            platillosAgrupados[keyPlatillo].instrucciones += ' | ' + item.instrucciones.trim();
          } else {
            platillosAgrupados[keyPlatillo].instrucciones = item.instrucciones.trim();
          }
        }
      });

      // Agregar platillos agrupados al array final
      Object.values(platillosAgrupados).forEach(platillo => {
        productosFinales.push(platillo);
      });
    });

    return productosFinales;
  }

  async procesarPendientes() {
    // Evitar overlap si el ciclo anterior aun no termina
    if (this.procesando) {
      return;
    }

    this.procesando = true;

    try {
      // 1. Obtener productos pendientes desde la API
      const pendientes = await API.comandaAuto.getPendientes();

      if (!pendientes || pendientes.length === 0) {
        return; // Nada que imprimir
      }

      this.log(`Detectados ${pendientes.length} productos pendientes de app`);

      // 2. Agrupar por idPedido + impresora
      const grupos = {};

      pendientes.forEach(item => {
        const impresora = this.resolverImpresora(item.impresora);
        const key = `${item.idPedido}_${impresora}`;

        if (!grupos[key]) {
          grupos[key] = {
            idPedido: item.idPedido,
            impresora: impresora,
            tipo: item.tipo || 'mesa',
            mesero: item.mesero || '',
            numeroPedido: item.numeroPedido || '',
            numeroMesa: item.numeroMesa || '',
            nombreMesa: item.nombreMesa || '',
            items: [],  // items sin agrupar (para luego agrupar por persona + platillo)
            ids: []
          };
        }

        grupos[key].items.push({
          nombre: item.nombrePlatillo,
          cantidad: parseInt(item.cantidad) || 1,
          observaciones: Array.isArray(item.observaciones) ? item.observaciones : [],
          instrucciones: item.textoLibre || '',
          idPlatillo: item.idPlatillo,
          numeroPersona: parseInt(item.numeroPersona) || 0
        });

        grupos[key].ids.push(item.idPedidoDetalle);
      });

      // 2b. Agrupar productos por persona y por platillo dentro de cada grupo
      for (const key of Object.keys(grupos)) {
        grupos[key].productos = this.agruparPorPersonaYPlatillo(grupos[key].items);
        delete grupos[key].items;
      }

      // 3. Recolectar TODOS los IDs que vamos a intentar imprimir
      const todosLosIds = [];
      for (const key of Object.keys(grupos)) {
        todosLosIds.push(...grupos[key].ids);
      }

      // 4. Marcar como impresos ANTES de imprimir para evitar reimpresiones
      //    (el comando de impresion llega a la impresora aunque el IPC haga timeout)
      if (todosLosIds.length > 0) {
        try {
          await API.comandaAuto.marcarImpresos(todosLosIds);
          this.log(`Marcados ${todosLosIds.length} productos como impresos (app=2, status=impreso)`);
        } catch (err) {
          this.log(`Error al marcar impresos, abortando impresion: ${err.message}`);
          return; // Si no pudimos marcar, no imprimir (evita reimprimir en loop)
        }
      }

      // 5. Imprimir todos los grupos en PARALELO (cada grupo va a una impresora diferente)
      const promesasImpresion = Object.keys(grupos).map(key => {
        const grupo = grupos[key];
        const fechaActual = new Date();
        const fecha = comandaService.formatearFecha(fechaActual);
        const hora = comandaService.formatearHora(fechaActual);

        const mesa = {
          nombre: grupo.nombreMesa || `Mesa ${grupo.numeroMesa}`,
          numero: grupo.numeroMesa
        };

        const mesero = {
          nombre: grupo.mesero
        };

        const infoPedido = {
          tipoPedido: grupo.tipo || 'mesa',
          nombreCliente: '',
          numeroPedido: grupo.numeroPedido,
          silentOnly: true // No abrir ventana del navegador como fallback
        };

        this.log(`Imprimiendo ${grupo.productos.length} productos del pedido #${grupo.idPedido} en ${grupo.impresora}`);

        return comandaService.imprimirComanda(
          grupo.productos,
          mesa,
          mesero,
          fecha,
          hora,
          grupo.impresora,
          infoPedido
        ).then(resultado => {
          if (resultado && resultado.success) {
            this.log(`OK - Pedido #${grupo.idPedido} impreso en ${grupo.impresora}`);
          } else {
            this.log(`Pedido #${grupo.idPedido} enviado a ${grupo.impresora} (timeout IPC, pero el comando llego a la impresora)`);
          }
        }).catch(err => {
          this.log(`Pedido #${grupo.idPedido} enviado a ${grupo.impresora} (${err.message})`);
        });
      });

      await Promise.allSettled(promesasImpresion);

    } catch (error) {
      // Solo loguear si no es un error de red trivial (la API puede no estar disponible)
      if (error.message && !error.message.includes('Network Error')) {
        this.log(`Error en ciclo: ${error.message}`);
      }
    } finally {
      this.procesando = false;
    }
  }
}

// Exportar singleton
const autoPrintService = new AutoPrintService();
export default autoPrintService;
